import { z } from "zod";

import { asMoney, asRateFraction } from "@bw/shared/types";
import type {
  ContractTermsResponse,
  OrgContractTermsVersionResponse,
  PublishContractVersionRequest,
} from "@bw/shared/types/generated";

export type PayoutFrequency =
  PublishContractVersionRequest["payout_schedule"]["frequency"];

const UNSIGNED_DECIMAL = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
const INTEGER = /^\d+$/;

function decimalParts(value: string): {
  whole: string;
  fraction: string;
} | null {
  const trimmed = value.trim();
  if (!UNSIGNED_DECIMAL.test(trimmed)) return null;
  const [rawWhole, fraction = ""] = trimmed.split(".");
  const whole = (rawWhole || "0").replace(/^0+(?=\d)/, "");
  return { whole, fraction };
}

export function fractionToPercent(value: string): string {
  // Pydantic may serialize a stored Decimal in scientific notation. Formatting
  // accepts that server representation; editable percentage input stays plain.
  const scientific = /^(\d+(?:\.\d*)?|\.\d+)[eE]([+-]?\d+)$/.exec(value.trim());
  const parts = decimalParts(scientific ? scientific[1] : value);
  if (!parts) return "";

  const digits = `${parts.whole}${parts.fraction}`;
  if (!/[1-9]/.test(digits)) return "0";
  const exponent = (scientific ? BigInt(scientific[2]) : 0n) + 2n;
  const point = BigInt(parts.whole.length) + exponent;
  // Keep extreme exponents exact without allocating an unbounded zero string.
  if (scientific && (point < -1000n || point > BigInt(digits.length) + 1000n)) {
    return `${scientific[1]}E${exponent}`;
  }
  const position = Number(point);
  const whole = (position <= 0 ? "0" : position >= digits.length
    ? digits.padEnd(position, "0") : digits.slice(0, position)).replace(/^0+(?=\d)/, "");
  const fraction = (position <= 0 ? `${"0".repeat(-position)}${digits}`
    : digits.slice(position)).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

export function isCommissionPercentValid(value: string): boolean {
  const parts = decimalParts(value);
  if (!parts) return false;
  return parts.whole.length < 3;
}

export function percentToFraction(value: string): string | null {
  const parts = decimalParts(value);
  if (!parts || parts.whole.length >= 3) return null;

  const paddedWhole = parts.whole.padStart(3, "0");
  const fractionWhole =
    paddedWhole.slice(0, -2).replace(/^0+(?=\d)/, "") || "0";
  const fractionDigits = `${paddedWhole.slice(-2)}${parts.fraction}`.replace(
    /0+$/,
    "",
  );
  return fractionDigits ? `${fractionWhole}.${fractionDigits}` : fractionWhole;
}

function isPositiveMoney(value: string): boolean {
  const parts = decimalParts(value);
  return Boolean(parts && /[1-9]/.test(`${parts.whole}${parts.fraction}`));
}

function isRealDate(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const parsed = new Date(`${trimmed}T00:00:00Z`);
  return (
    !globalThis.Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === trimmed
  );
}

function integerInRange(value: string, minimum: number, maximum: number) {
  const trimmed = value.trim();
  if (!INTEGER.test(trimmed)) return false;
  const parsed = globalThis.parseInt(trimmed, 10);
  return parsed >= minimum && parsed <= maximum;
}

export const contractTermsDraftSchema = z
  .object({
    commissionPercent: z.string().refine(isCommissionPercentValid, {
      message: "Enter a plain percentage from 0 up to, but not including, 100.",
    }),
    effectiveDate: z.string().refine(isRealDate, {
      message: "Enter a real date in YYYY-MM-DD format.",
    }),
    frequency: z.enum(["daily", "weekly", "biweekly", "monthly"]),
    minimumPayout: z.string().refine(isPositiveMoney, {
      message: "Enter a plain decimal amount greater than 0.",
    }),
    timezone: z.string().refine((value) => Boolean(value.trim()), {
      message: "Enter the payout timezone.",
    }),
    dayOfWeek: z.string(),
    dayOfMonth: z.string(),
    serviceFeeBindings: z
      .array(
        z.custom<
          NonNullable<
            PublishContractVersionRequest["service_fee_bindings"]
          >[number]
        >(),
      )
      .optional(),
    documentContent: z.string().max(131_072).refine(
      (value) => Boolean(value.trim()),
      { message: "Paste the exact reviewed agreement." },
    ),
    documentTemplateKey: z.string().trim().min(1, "Enter the document template key."),
    documentTemplateVersion: z.string().trim().min(1, "Enter the document version."),
    documentRenderingVersion: z.string().trim().min(1, "Enter the rendering version."),
    legalPartyReference: z.string().trim().min(1, "Enter the approved legal-party reference."),
    jurisdictionReferences: z.string().refine(
      (value) => value.split(/[,\n]/).some((entry) => Boolean(entry.trim())),
      { message: "Enter at least one approved jurisdiction reference." },
    ),
    language: z.string().trim().min(1, "Enter the document language."),
    approvalReference: z.string().trim().min(1, "Enter the legal approval reference."),
  })
  .superRefine((draft, context) => {
    const usesWeekday =
      draft.frequency === "weekly" || draft.frequency === "biweekly";
    if (usesWeekday && !integerInRange(draft.dayOfWeek, 0, 6)) {
      context.addIssue({
        code: "custom",
        path: ["dayOfWeek"],
        message: "Choose a weekday from Monday through Sunday.",
      });
    }

    if (
      draft.frequency === "monthly" &&
      !integerInRange(draft.dayOfMonth, 1, 31)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dayOfMonth"],
        message: "Enter a day from 1 through 31.",
      });
    }
  });

export type ContractTermsDraft = z.infer<typeof contractTermsDraftSchema>;

function latestKnownEffectiveDate(
  currentTerms: ContractTermsResponse | null,
  today: string,
  latestHistoryEffectiveDate: string | null,
): string {
  const candidates = [
    today,
    currentTerms?.effective_date,
    currentTerms?.upcoming_effective_date,
    latestHistoryEffectiveDate,
  ];
  let latest = "";

  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const date = candidate.trim();
    if (isRealDate(date) && date > latest) latest = date;
  }

  return latest || today;
}

export function latestContractHistoryEffectiveDate(
  versions: readonly OrgContractTermsVersionResponse[],
): string | null {
  let latest: string | null = null;

  for (const version of versions) {
    if (typeof version.effective_date !== "string") continue;
    const date = version.effective_date.trim();
    if (isRealDate(date) && (latest === null || date > latest)) latest = date;
  }

  return latest;
}

export function createContractTermsDraft(
  currentTerms: ContractTermsResponse | null,
  today: string,
  latestHistoryEffectiveDate: string | null,
): ContractTermsDraft {
  const schedule = currentTerms?.payout_schedule;
  return {
    commissionPercent: currentTerms
      ? fractionToPercent(currentTerms.commission_rate)
      : "",
    effectiveDate: latestKnownEffectiveDate(
      currentTerms,
      today,
      latestHistoryEffectiveDate,
    ),
    frequency: schedule?.frequency ?? "monthly",
    minimumPayout: schedule ? String(schedule.min_payout_amount) : "25",
    timezone: schedule?.timezone ?? "UTC",
    dayOfWeek:
      schedule?.day_of_week == null ? "0" : String(schedule.day_of_week),
    dayOfMonth:
      schedule?.day_of_month == null ? "1" : String(schedule.day_of_month),
    serviceFeeBindings:
      currentTerms?.service_fee_bindings?.map((binding) =>
        structuredClone(binding),
      ) ?? [],
    documentContent: currentTerms?.document?.content ?? "",
    documentTemplateKey:
      currentTerms?.document?.template_key ?? "organization-services-agreement",
    documentTemplateVersion: currentTerms?.document?.template_version ?? "",
    documentRenderingVersion:
      currentTerms?.document?.rendering_version ?? "plain-text-v1",
    legalPartyReference: currentTerms?.document?.legal_party_reference ?? "",
    jurisdictionReferences:
      currentTerms?.document?.jurisdiction_references?.join(", ") ?? "",
    language: currentTerms?.document?.language ?? "en-US",
    approvalReference: currentTerms?.document?.approval_reference ?? "",
  };
}

export function buildContractVersionRequest(
  draft: ContractTermsDraft,
): PublishContractVersionRequest | null {
  const parsedDraft = contractTermsDraftSchema.safeParse(draft);
  if (!parsedDraft.success) return null;

  const values = parsedDraft.data;
  const commission = percentToFraction(values.commissionPercent);
  const minimumPayout = asMoney(values.minimumPayout.trim());
  if (!commission || !minimumPayout) return null;

  const usesWeekday =
    values.frequency === "weekly" || values.frequency === "biweekly";
  const usesMonthDay = values.frequency === "monthly";

  return {
    commission_rate: asRateFraction(commission)!,
    effective_date: values.effectiveDate.trim(),
    service_fee_bindings: values.serviceFeeBindings ?? [],
    payout_schedule: {
      frequency: values.frequency,
      day_of_week: usesWeekday
        ? globalThis.parseInt(values.dayOfWeek.trim(), 10)
        : null,
      day_of_month: usesMonthDay
        ? globalThis.parseInt(values.dayOfMonth.trim(), 10)
        : null,
      timezone: values.timezone.trim(),
      min_payout_amount: minimumPayout,
    },
    document_publication: {
      document: {
        content: values.documentContent,
        media_type: "text/plain",
        template_key: values.documentTemplateKey.trim(),
        template_version: values.documentTemplateVersion.trim(),
        rendering_version: values.documentRenderingVersion.trim(),
        legal_party_reference: values.legalPartyReference.trim(),
        jurisdiction_references: values.jurisdictionReferences
          .split(/[,\n]/)
          .map((value) => value.trim())
          .filter(Boolean),
        language: values.language.trim(),
      },
      approval_reference: values.approvalReference.trim(),
    },
  };
}
